import React, { useState, useMemo, useCallback, memo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { AssignmentTable, type ApiAssignment } from "@/components/AssignmentTable";
import { DataTable } from "@/components/tables/data-table";
import { createMobileAssignmentColumns } from "@/components/tables/mobile-assignment-columns";
import type { MobileAssignment } from "@/components/tables/mobile-assignment-columns";
import { Archive, History, RefreshCw, Filter, X, Loader2, Smartphone, List, Eye, ClipboardList } from "lucide-react";
import { YearFilter } from "@/components/filters/YearFilter";
import { useYear } from "@/state/yearStore";
import { useUrlYear } from "@/hooks/useUrlYear";

// Types based on your spec
interface Assignment {
  id: number;
  campaignId: number;
  campaignName: string;
  storeId: number;
  storeName: string;
  operatorId: number;
  operatorName: string;
  packId: number;
  packName: string;
  taskId: number;
  taskName: string;
  targetQuantity: number;
  status: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  // Phase 4: Task type and removal context
  taskType?: 'POSTING' | 'REMOVAL';
  referenceCycleId?: number | null;
  assignmentSource?: 'AUTO_FROM_POSTING' | 'MANUAL_OVERRIDE';
  // Fields for derived status display
  mobile_assignment_id?: number | null;
  mobileAssignmentId?: number | null;
  sent_to_pdf_at?: string | null;
  sentToPdfAt?: string | null;
}

// ============================================================
// Derived Display Status (Phase 2 Follow-up)
// ============================================================
// Computes user-friendly status labels from canonical DB status + metadata
// DB stays canonical (6 statuses), but UI shows operator-facing sub-states
function deriveDisplayStatus(assignment: Assignment): string {
  const base = assignment.status;

  // Check metadata fields for sub-state indicators
  const isMobileAssignment = !!assignment.mobileAssignmentId || !!assignment.mobile_assignment_id;
  const isPdf = !!assignment.sentToPdfAt || !!assignment.sent_to_pdf_at;
  const isLocked = base === 'locked';
  const isOperatorComplete = base === 'operator_complete';
  const isAdminComplete = base === 'admin_complete';
  const isAdminRejected = base === 'admin_rejected';
  const isArchived = base === 'archived';

  // Terminal states (no sub-states)
  if (isArchived) return 'Archived';
  if (isLocked) return 'Locked';
  if (isAdminComplete) return 'Admin Complete';
  if (isAdminRejected) return 'Admin Rejected';
  if (isOperatorComplete) return 'Operator Complete';

  // Pending sub-states (derived from metadata)
  if (base === 'pending' && isMobileAssignment) return 'Sent to Operator (Mobile)';
  if (base === 'pending' && isPdf) return 'Sent to Operator (PDF)';
  if (base === 'pending') return 'Pending (Not Sent)';

  // Fallback to canonical status
  return base.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

interface AssignmentFilters {
  cycleId?: number;
  operatorId?: number;
  status?: string;
  retailerId?: number;
  includeDeleted?: boolean;
  taskType?: 'POSTING' | 'REMOVAL';  // Phase 4: Filter by task type
}

interface HistoryEntry {
  oldStatus: string;
  newStatus: string;
  changedBy: string;
  timestamp: string;
  reason?: string;
}

type TabView = 'all' | 'mobile';

export default function AssignmentManagement() {
  // Global year filter
  const year = useYear();
  useUrlYear();

  const initialTab = (() => {
    const tab = new URLSearchParams(window.location.search).get('tab');
    return tab === 'mobile' ? 'mobile' : 'all';
  })();
  const focusedAssignmentId = new URLSearchParams(window.location.search).get('assignmentId');

  const [activeTab, setActiveTab] = useState<TabView>(initialTab);
  const [filters, setFilters] = useState<AssignmentFilters>({});
  const [selectedAssignments, setSelectedAssignments] = useState<number[]>([]);
  const [bulkActionOpen, setBulkActionOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<number | null>(null);
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkReason, setBulkReason] = useState("");
  const { toast } = useToast();
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedMobileAssignment, setSelectedMobileAssignment] = useState<MobileAssignment | null>(null);
  const [previewAssignments, setPreviewAssignments] = useState<Assignment[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [evidenceModalOpen, setEvidenceModalOpen] = useState(false);
  const [selectedEvidenceId, setSelectedEvidenceId] = useState<number | null>(null);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [evidenceData, setEvidenceData] = useState<any>(null);

  // Phase 4: Reassignment modal state
  const [reassignModalOpen, setReassignModalOpen] = useState(false);
  const [selectedReassignAssignment, setSelectedReassignAssignment] = useState<Assignment | null>(null);
  const [reassignOperatorId, setReassignOperatorId] = useState<string>("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(200);

  // Fetch assignments with filters and pagination (Assignments tab only)
  const { data: assignmentsData, isLoading, refetch } = useQuery({
    queryKey: ["/api/admin/assignments", filters, currentPage, pageSize, year],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.cycleId) params.append('cycleId', filters.cycleId.toString());
      if (filters.operatorId) params.append('operatorId', filters.operatorId.toString());
      if (filters.status) params.append('status', filters.status);
      if (filters.retailerId) params.append('retailerId', filters.retailerId.toString());
      if (filters.includeDeleted) params.append('includeDeleted', 'true');
      if (filters.taskType) params.append('taskType', filters.taskType);
      params.append('year', year.toString());

      // Add pagination params
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());

      const response = await apiRequest(`/api/admin/assignments?${params.toString()}`, 'GET');
      return response.json();
    },
    // Move data transformation into select for stable references and memoization
    select: useCallback((data: any) => {
      // Handle both old format (array) and new format (object with pagination)
      if (Array.isArray(data)) {
        return {
          assignments: data as Assignment[],
          pagination: {
            page: 1,
            totalPages: 1,
            totalRecords: data.length,
            hasNextPage: false,
            hasPreviousPage: false
          }
        };
      }
      return {
        assignments: (data.assignments ?? []) as Assignment[],
        pagination: data.pagination ?? {
          page: 1,
          totalPages: 1,
          totalRecords: 0,
          hasNextPage: false,
          hasPreviousPage: false
        }
      };
    }, []),
    enabled: activeTab === 'all',
    staleTime: 30000, // Cache for 30 seconds
  });

  // Extract processed assignments and pagination from select
  const assignments = assignmentsData?.assignments ?? [];
  const pagination = assignmentsData?.pagination ?? {
    page: 1,
    totalPages: 1,
    totalRecords: 0,
    hasNextPage: false,
    hasPreviousPage: false
  };

  // Fetch mobile assignments (sent to operators)
  const { data: mobileAssignments = [], isLoading: isLoadingMobile, refetch: refetchMobile, isFetching: isFetchingMobile } = useQuery<MobileAssignment[]>({
    queryKey: ["/api/admin/mobile-assignments", filters, year],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.cycleId) params.append('cycleId', filters.cycleId.toString());
      if (filters.operatorId) params.append('operatorId', filters.operatorId.toString());
      if (filters.retailerId) params.append('retailerId', filters.retailerId.toString());
      params.append('year', year.toString());

      const response = await apiRequest(`/api/admin/mobile-assignments?${params.toString()}`, 'GET');
      const data = await response.json();
      // Map backend field names to frontend interface (lastUpdatedAt -> lastSentAt)
      const mapped = Array.isArray(data) ? data.map((item: any) => ({
        ...item,
        lastSentAt: item.lastUpdatedAt || item.sentMobileAt || item.sentPdfAt || null,
      })) : [];
      return mapped;
    },
    enabled: activeTab === 'mobile',
    staleTime: 30_000,
    // Retry configuration for better reliability
    retry: 1,
    retryDelay: 1000,
  });

  // Fetch cycles for filter dropdown (filtered by retailer if selected)
  const { data: cycles = [] } = useQuery({
    queryKey: ["/api/booking-cycles", filters.retailerId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.retailerId) params.append('retailerId', filters.retailerId.toString());
      const response = await apiRequest(`/api/booking-cycles?${params.toString()}`, 'GET');
      return response.json();
    },
    staleTime: 30_000,
  });

  // Fetch operators for filter dropdown
  const { data: operators = [] } = useQuery({
    queryKey: ["/api/users/operators"],
    queryFn: async () => {
      const response = await apiRequest('/api/users/operators', 'GET');
      return response.json();
    },
    staleTime: 30_000,
  });

  // Fetch retailers for filter dropdown
  const { data: retailers = [] } = useQuery({
    queryKey: ["/api/retailers"],
    queryFn: async () => {
      const response = await apiRequest('/api/retailers', 'GET');
      return response.json();
    },
    staleTime: 30_000,
  });

  // Fetch assignment history
  const { data: assignmentHistory = [] } = useQuery<HistoryEntry[]>({
    queryKey: ["/api/admin/assignments", selectedAssignmentId, "history"],
    queryFn: async () => {
      if (!selectedAssignmentId) return [];
      const response = await apiRequest(`/api/admin/assignments/${selectedAssignmentId}/history`, 'GET');
      const data = await response.json();
      return data.history ?? [];
    },
    enabled: !!selectedAssignmentId && historyModalOpen,
  });

  // Bulk status update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: async ({ assignmentIds, newStatus, reason }: { assignmentIds: number[]; newStatus: string; reason?: string }) => {
      const response = await apiRequest('/api/admin/assignments/bulk-status', 'PATCH', {
        assignmentIds,
        newStatus,
        reason,
      });

      const result = await response.json();
      if (!result?.success) {
        const message = result?.error || 'Bulk update failed with an unknown error';
        throw Object.assign(new Error(message), { data: result });
      }

      return result as { success: true; updatedCount?: number; assignmentIds?: number[]; newStatus: string; message?: string };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/assignments"] });
      setSelectedAssignments([]);
      setBulkActionOpen(false);
      setBulkStatus("");
      setBulkReason("");

      const friendlyStatus = data?.newStatus
        ? data.newStatus.replace(/_/g, " ")
        : undefined;
      const friendlyCount = data?.updatedCount;
      toast({
        title: "Success",
        description:
          data?.message ??
          (friendlyCount != null
            ? `${friendlyCount} assignment${friendlyCount === 1 ? "" : "s"} updated to ${friendlyStatus}`
            : friendlyStatus
              ? `Assignments updated to ${friendlyStatus}`
              : "Assignments updated successfully"),
      });
    },
    onError: (error: any) => {
      const description =
        error?.data?.error ||
        (Array.isArray(error?.data?.details)
          ? error.data.details.map((d: any) => d?.message).filter(Boolean).join("\n")
          : undefined) ||
        error?.message ||
        'Failed to update assignments';
      toast({
        title: "Error",
        description,
        variant: "destructive",
      });
    },
  });

  // Individual assignment update mutation
  const updateAssignmentMutation = useMutation({
    mutationFn: async ({ id, status, adminNotes }: { id: number, status?: string, adminNotes?: string }) => {
      const response = await apiRequest(`/api/admin/assignments/${id}`, 'PATCH', {
        status,
        adminNotes,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/assignments"] });
      toast({
        title: "Success",
        description: "Assignment updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update assignment",
        variant: "destructive",
      });
    },
  });

  // Soft delete mutation
  const deleteAssignmentMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest(`/api/admin/assignments/${id}`, 'DELETE');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/assignments"] });
      toast({
        title: "Success",
        description: "Assignment deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete assignment",
        variant: "destructive",
      });
    },
  });

  // Phase 4: Reassign operator mutation
  const reassignOperatorMutation = useMutation({
    mutationFn: async ({ id, operatorId }: { id: number; operatorId: number }) => {
      const response = await apiRequest(`/api/admin/assignments/${id}`, 'PATCH', {
        operatorId,
      });
      const result = await response.json();
      if (!result?.success) {
        throw new Error(result?.error || 'Reassignment failed');
      }
      return result;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/assignments"] });
      setReassignModalOpen(false);
      setSelectedReassignAssignment(null);
      setReassignOperatorId("");
      toast({
        title: "Success",
        description: "Assignment reassigned successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to reassign assignment",
        variant: "destructive",
      });
    },
  });

  const handleBulkAction = () => {
    if (selectedAssignments.length === 0) {
      toast({
        title: "Error",
        description: "Please select assignments to update",
        variant: "destructive",
      });
      return;
    }

    if (!bulkStatus) {
      toast({
        title: "Error",
        description: "Please select a status",
        variant: "destructive",
      });
      return;
    }

    bulkUpdateMutation.mutate({
      assignmentIds: selectedAssignments,
      newStatus: bulkStatus,
      reason: bulkReason,
    });
  };

  // Memoized handlers for AssignmentTable (Assignments tab only)
  const handleStatusChange = useCallback((assignmentId: number, newStatus: string) => {
    updateAssignmentMutation.mutate({
      id: assignmentId,
      status: newStatus,
    });
  }, [updateAssignmentMutation]);

  const handleViewHistory = useCallback((assignmentId: number) => {
    setSelectedAssignmentId(assignmentId);
    setHistoryModalOpen(true);
  }, []);

  const handleDelete = useCallback((assignmentId: number) => {
    if (confirm("Are you sure you want to delete this assignment?")) {
      deleteAssignmentMutation.mutate(assignmentId);
    }
  }, [deleteAssignmentMutation]);

  // Phase 4: Handler for opening reassign modal
  const handleReassign = useCallback((assignment: any) => {
    // Normalize to Assignment shape
    setSelectedReassignAssignment({
      ...assignment,
      id: Number(assignment.id),
      campaignId: Number(assignment.campaignId),
      storeId: Number(assignment.storeId),
      operatorId: Number(assignment.operatorId),
      packId: Number(assignment.packId),
      taskId: Number(assignment.taskId),
    } as Assignment);
    setReassignOperatorId(assignment.operatorId?.toString() || "");
    setReassignModalOpen(true);
  }, []);

  // Phase 4: Handler for confirming reassignment
  const handleConfirmReassign = useCallback(() => {
    if (!selectedReassignAssignment || !reassignOperatorId) return;
    
    const newOperatorId = parseInt(reassignOperatorId, 10);
    if (isNaN(newOperatorId)) {
      toast({
        title: "Error",
        description: "Please select a valid operator",
        variant: "destructive",
      });
      return;
    }

    reassignOperatorMutation.mutate({
      id: selectedReassignAssignment.id,
      operatorId: newOperatorId,
    });
  }, [selectedReassignAssignment, reassignOperatorId, reassignOperatorMutation, toast]);

  const handlePreviewMobileAssignment = useCallback(async (assignment: MobileAssignment) => {
    setSelectedMobileAssignment(assignment);
    setPreviewModalOpen(true);
    setPreviewLoading(true);
    setPreviewAssignments([]);

    try {
      // Fetch the actual assignment details for these assignment IDs.
      // Pass limit=500 to bypass the default page size (50) - we want all IDs in one shot.
      const response = await apiRequest(
        `/api/admin/assignments?ids=${assignment.assignmentIds?.join(',') || ''}&limit=500`,
        'GET'
      );
      const data = await response.json();

      // Backend returns { success: true, assignments: [...], pagination: {...} }
      const assignmentsList = data.assignments || [];
      console.log('[PREVIEW] Fetched', assignmentsList.length, 'of', assignment.assignmentsCount, 'assignments for preview');
      setPreviewAssignments(assignmentsList);
    } catch (error) {
      console.error('Failed to fetch preview assignments:', error);
      toast({
        title: "Error",
        description: "Failed to load assignment details",
        variant: "destructive",
      });
    } finally {
      setPreviewLoading(false);
    }
  }, [toast]);

  // Memoize mobile assignment columns
  const mobileAssignmentColumns = useMemo(
    () => createMobileAssignmentColumns({
      onPreview: handlePreviewMobileAssignment,
    }),
    [handlePreviewMobileAssignment]
  );

  const handleViewEvidence = useCallback((assignmentId: number) => {
    setSelectedEvidenceId(assignmentId);
    setEvidenceModalOpen(true);

    // Fetch evidence for this assignment
    setEvidenceLoading(true);
    setEvidenceData(null);

    fetch(`/api/admin/assignment-evidence?taskId=${assignmentId}`, { credentials: 'omit' })
      .then(res => res.json())
      .then(data => {
        setEvidenceData(data);
        setEvidenceLoading(false);
      })
      .catch(error => {
        console.error('Error loading evidence:', error);
        setEvidenceLoading(false);
        toast({
          title: "Error",
          description: "Failed to load evidence",
          variant: "destructive",
        });
      });
  }, [toast]);

  // Updated to accept assignment object for derived status display
  // Memoized to prevent recalculation on every render
  const getStatusBadge = useCallback((assignment: ApiAssignment | string) => {
    // Support both assignment object and plain status string for backward compatibility
    const status = typeof assignment === 'string' ? assignment : (assignment.status ?? 'pending');
    const displayStatus = typeof assignment === 'string'
      ? assignment.replace(/_/g, " ").toUpperCase()
      : deriveDisplayStatus({ ...assignment, status } as Assignment);

    // Map canonical statuses to colors (derived display handled in text)
    const statusColors: Record<string, string> = {
      pending: "bg-gray-100 text-gray-800",
      operator_complete: "bg-yellow-100 text-yellow-800",
      admin_complete: "bg-green-100 text-green-800",
      admin_rejected: "bg-red-100 text-red-800",
      locked: "bg-blue-100 text-blue-800",
      archived: "bg-purple-100 text-purple-800",
    };

    return (
      <Badge className={statusColors[status] || "bg-gray-100 text-gray-800"}>
        {displayStatus}
      </Badge>
    );
  }, []);

  const currentLoading = activeTab === 'all' ? isLoading : isLoadingMobile;
  const currentRefetch = activeTab === 'all' ? refetch : refetchMobile;

  if (currentLoading) {
    return (
      <div className="p-4 lg:p-6 space-y-6">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading {activeTab === 'all' ? 'assignments' : 'mobile assignments'}...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <PageHeader
        icon={ClipboardList}
        title="Assignment Management"
        description={
          activeTab === 'all' ? (
            <>
              Total: {pagination.totalRecords} assignments (showing {assignments.length})
              {selectedAssignments.length > 0 && ` | Selected: ${selectedAssignments.length}`}
            </>
          ) : (
            <>
              Total: {mobileAssignments.length} operator groups
              {isFetchingMobile && ' (updating...)'}
            </>
          )
        }
      >
        <Button onClick={() => currentRefetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
        {activeTab === 'all' && selectedAssignments.length > 0 && (
          <Button onClick={() => setBulkActionOpen(true)}>
            <Archive className="h-4 w-4 mr-2" />
            Bulk Actions ({selectedAssignments.length})
          </Button>
        )}
      </PageHeader>

      {/* Tab Bar */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as TabView)} className="w-full">
        <TabsList>
          <TabsTrigger value="all">
            <List className="h-4 w-4 mr-2" />
            All Assignments
          </TabsTrigger>
          <TabsTrigger value="mobile">
            <Smartphone className="h-4 w-4 mr-2" />
            Mobile Assignments
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {focusedAssignmentId && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Looking for repost task #{focusedAssignmentId}. Use the Mobile Assignments tab and operator/cycle filters to review, recall, or resolve it before changing the original verdict.
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Year Filter */}
          <div className="mb-4 pb-4 border-b">
            <YearFilter />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label>Retailer</Label>
              <Select
                value={filters.retailerId != null ? filters.retailerId.toString() : "all"}
                onValueChange={(value) => {
                  setFilters(prev => ({
                    ...prev,
                    retailerId: value === "all" ? undefined : parseInt(value),
                    cycleId: undefined // Clear cycle when retailer changes
                  }));
                  setCurrentPage(1); // Reset to first page on filter change
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All retailers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All retailers</SelectItem>
                  {retailers.map((retailer: any) => (
                    <SelectItem key={retailer.id} value={retailer.id.toString()}>
                      {retailer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Cycle</Label>
              <Select
                value={filters.cycleId != null ? filters.cycleId.toString() : "all"}
                onValueChange={(value) => {
                  setFilters(prev => ({
                    ...prev,
                    cycleId: value === "all" ? undefined : parseInt(value)
                  }));
                  setCurrentPage(1); // Reset to first page on filter change
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All cycles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All cycles</SelectItem>
                  {cycles.map((cycle: any) => (
                    <SelectItem key={cycle.id} value={cycle.id.toString()}>
                      {cycle.name || `Cycle ${cycle.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Operator</Label>
              <Select
                value={filters.operatorId != null ? filters.operatorId.toString() : "all"}
                onValueChange={(value) => {
                  setFilters(prev => ({
                    ...prev,
                    operatorId: value === "all" ? undefined : parseInt(value)
                  }));
                  setCurrentPage(1); // Reset to first page on filter change
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All operators" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All operators</SelectItem>
                  {operators.map((operator: any) => (
                    <SelectItem key={operator.id} value={operator.id.toString()}>
                      {operator.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {activeTab === 'all' && (
              <div>
                <Label>Status</Label>
                <Select
                  value={filters.status ?? "all"}
                  onValueChange={(value) => {
                    setFilters(prev => ({
                      ...prev,
                      status: value === "all" ? undefined : value
                    }));
                    setCurrentPage(1); // Reset to first page on filter change
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="pending">Pending (all)</SelectItem>
                    <SelectItem value="operator_complete">Operator Complete</SelectItem>
                    <SelectItem value="admin_complete">Admin Complete</SelectItem>
                    <SelectItem value="admin_rejected">Admin Rejected</SelectItem>
                    <SelectItem value="locked">Locked</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Phase 4: Task Type filter */}
            {activeTab === 'all' && (
              <div>
                <Label>Task Type</Label>
                <Select
                  value={filters.taskType ?? "all"}
                  onValueChange={(value) => {
                    setFilters(prev => ({
                      ...prev,
                      taskType: value === "all" ? undefined : value as 'POSTING' | 'REMOVAL'
                    }));
                    setCurrentPage(1); // Reset to first page on filter change
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="POSTING">Posting</SelectItem>
                    <SelectItem value="REMOVAL">Removal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {activeTab === 'all' && (
            <div className="flex items-center space-x-2 mt-4">
              <Checkbox
                id="includeDeleted"
                checked={filters.includeDeleted || false}
                onCheckedChange={(checked) => setFilters(prev => ({ ...prev, includeDeleted: !!checked }))}
              />
              <Label htmlFor="includeDeleted">Include deleted assignments</Label>
            </div>
          )}

          {Object.keys(filters).some(key => filters[key as keyof AssignmentFilters]) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setFilters({});
                setCurrentPage(1); // Reset to first page when clearing filters
              }}
              className="mt-4"
            >
              <X className="h-4 w-4 mr-2" />
              Clear Filters
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Assignment Tables */}
      {activeTab === 'all' ? (
        <Card>
          <CardHeader>
            <CardTitle>Assignments</CardTitle>
          </CardHeader>
          <CardContent>
            <AssignmentTable
              assignments={assignments}
              selectedAssignments={selectedAssignments}
              onSelectionChange={setSelectedAssignments}
              onStatusChange={handleStatusChange}
              onViewHistory={handleViewHistory}
              onDelete={handleDelete}
              onReassign={handleReassign}
              getStatusBadge={getStatusBadge}
              showActions={true}
            />

            {/* Pagination Controls */}
            <div className="flex items-center justify-between px-4 py-4 border-t border-gray-200 mt-4">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-700">
                  Showing {assignments.length === 0 ? 0 : ((pagination.page - 1) * pageSize) + 1} to{' '}
                  {Math.min(pagination.page * pageSize, pagination.totalRecords)} of{' '}
                  {pagination.totalRecords} assignments
                </span>

                <div className="flex items-center gap-2">
                  <Label className="text-sm text-gray-600">Per page:</Label>
                  <Select
                    value={pageSize.toString()}
                    onValueChange={(v) => {
                      setPageSize(parseInt(v));
                      setCurrentPage(1); // Reset to first page on size change
                    }}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                      <SelectItem value="200">200</SelectItem>
                      <SelectItem value="500">500</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={!pagination.hasPreviousPage || isLoading}
                >
                  Previous
                </Button>

                <span className="text-sm text-gray-700 px-3">
                  Page {pagination.page} of {pagination.totalPages}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={!pagination.hasNextPage || isLoading}
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Mobile Assignments (Sent to Operators)</span>
              {isFetchingMobile && (
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingMobile ? (
              <div className="text-center py-12">
                <Loader2 className="h-12 w-12 mx-auto mb-4 text-gray-400 animate-spin" />
                <p className="text-lg font-medium text-gray-900">Loading mobile assignments...</p>
                <p className="text-sm text-gray-600 mt-2">Fetching data from server</p>
              </div>
            ) : mobileAssignments.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Smartphone className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium">No mobile assignments found</p>
                <p className="text-sm mt-2">Tasks will appear here after admin sends them to operators</p>
              </div>
            ) : (
              <DataTable
                columns={mobileAssignmentColumns}
                data={mobileAssignments}
                searchKey="operatorName"
                searchPlaceholder="Search by operator..."
                showColumnToggle={true}
                isLoading={isLoadingMobile}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Bulk Action Modal */}
      <Dialog open={bulkActionOpen} onOpenChange={setBulkActionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Update Assignments</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New Status</Label>
              <Select value={bulkStatus} onValueChange={setBulkStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="ready_for_operator">Ready for Operator</SelectItem>
                  <SelectItem value="sent_to_operator">Sent to Operator</SelectItem>
                  <SelectItem value="sent_to_operator_pdf">Sent to Operator (PDF)</SelectItem>
                  <SelectItem value="sent_to_operator_mobile">Sent to Operator (Mobile)</SelectItem>
                  <SelectItem value="operator_complete">Operator Complete</SelectItem>
                  <SelectItem value="admin_complete">Admin Complete</SelectItem>
                  <SelectItem value="admin_approved">Admin Approved</SelectItem>
                  <SelectItem value="archived_not_actioned">Archived (Not Actioned)</SelectItem>
                  <SelectItem value="archived_completed">Archived (Completed)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Reason (Optional)</Label>
              <Textarea
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                placeholder="Enter reason for bulk update..."
              />
            </div>

            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setBulkActionOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleBulkAction} disabled={bulkUpdateMutation.isPending}>
                {bulkUpdateMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating…
                  </>
                ) : (
                  <>Update {selectedAssignments.length} Assignments</>
                )}
              </Button>
            </div>
            {bulkUpdateMutation.isPending && (
              <p className="text-sm text-gray-500">Hold tight—this may take a few seconds for large selections.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* History Modal */}
      <Dialog open={historyModalOpen} onOpenChange={setHistoryModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Assignment History</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {assignmentHistory.length === 0 ? (
              <p className="text-gray-500">No history available</p>
            ) : (
              assignmentHistory.map((entry, index) => (
                <div key={index} className="border-l-2 border-blue-200 pl-4 pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium">{entry.changedBy}</span>
                      <span className="text-sm text-gray-500 ml-2">
                        {new Date(entry.timestamp).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="mt-1">
                    <span className="text-sm">
                      Changed from <Badge variant="outline">{entry.oldStatus}</Badge> to{" "}
                      <Badge variant="outline">{entry.newStatus}</Badge>
                    </span>
                  </div>
                  {entry.reason && (
                    <div className="mt-1 text-sm text-gray-600">
                      Reason: {entry.reason}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Mobile Assignment Preview Modal */}
      <Dialog open={previewModalOpen} onOpenChange={setPreviewModalOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Assignment Preview
              {selectedMobileAssignment && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  — {selectedMobileAssignment.operatorName} &middot; {selectedMobileAssignment.cycleName}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {previewLoading ? (
            <div className="text-center py-12">
              <Loader2 className="h-12 w-12 mx-auto mb-4 text-gray-400 animate-spin" />
              <p className="text-lg font-medium text-gray-900">Loading assignments...</p>
            </div>
          ) : selectedMobileAssignment && previewAssignments.length > 0 ? (
            (() => {
              // Summary stats
              const totalLoaded = previewAssignments.length;
              const totalExpected = selectedMobileAssignment.assignmentsCount ?? 0;

              // Group by campaign, then store within each campaign
              const byCampaign = new Map<string, { campaignId: number; tasks: Assignment[] }>();
              for (const task of previewAssignments) {
                const key = task.campaignName || `Campaign ${task.campaignId}`;
                if (!byCampaign.has(key)) byCampaign.set(key, { campaignId: task.campaignId, tasks: [] });
                byCampaign.get(key)!.tasks.push(task);
              }
              // Sort campaigns alphabetically
              const sortedCampaigns = Array.from(byCampaign.entries()).sort(([a], [b]) => a.localeCompare(b));

              return (
                <div className="space-y-4">
                  {/* Summary bar */}
                  <div className="flex items-center gap-4 text-sm bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                    <span className="font-medium text-blue-800">
                      Showing {totalLoaded}{totalLoaded < totalExpected ? ` of ${totalExpected}` : ''} tasks
                    </span>
                    <span className="text-blue-600">&middot; {sortedCampaigns.length} campaign{sortedCampaigns.length !== 1 ? 's' : ''}</span>
                    <span className="text-blue-600">&middot; {new Set(previewAssignments.map(a => a.storeId)).size} store{new Set(previewAssignments.map(a => a.storeId)).size !== 1 ? 's' : ''}</span>
                    {totalLoaded < totalExpected && (
                      <span className="ml-auto text-amber-700 font-medium">⚠ {totalExpected - totalLoaded} tasks not shown (increase limit)</span>
                    )}
                  </div>

                  {/* Campaign groups */}
                  {sortedCampaigns.map(([campaignName, { tasks }]) => {
                    // Group stores within this campaign
                    const byStore = new Map<string, Assignment[]>();
                    for (const task of tasks) {
                      const storeKey = task.storeName || `Store ${task.storeId}`;
                      if (!byStore.has(storeKey)) byStore.set(storeKey, []);
                      byStore.get(storeKey)!.push(task);
                    }
                    const sortedStores = Array.from(byStore.entries()).sort(([a], [b]) => a.localeCompare(b));

                    return (
                      <div key={campaignName} className="border border-gray-200 rounded-lg overflow-hidden">
                        {/* Campaign header */}
                        <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center justify-between">
                          <span className="font-semibold text-gray-900">{campaignName}</span>
                          <span className="text-sm text-gray-500">
                            {tasks.length} task{tasks.length !== 1 ? 's' : ''} &middot; {sortedStores.length} store{sortedStores.length !== 1 ? 's' : ''}
                          </span>
                        </div>

                        {/* Stores within campaign */}
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-100 text-left text-xs text-gray-600 uppercase tracking-wide">
                              <th className="px-4 py-2">Store</th>
                              <th className="px-4 py-2">Task ID</th>
                              <th className="px-4 py-2">Pack</th>
                              <th className="px-4 py-2">Type</th>
                              <th className="px-4 py-2">Qty</th>
                              <th className="px-4 py-2">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedStores.map(([storeName, storeTasks]) =>
                              storeTasks.map((task, idx) => (
                                <tr
                                  key={task.id}
                                  className={`border-t border-gray-100 ${idx === 0 ? 'border-t-gray-300' : ''} hover:bg-gray-50`}
                                >
                                  <td className="px-4 py-2 font-medium text-gray-800">
                                    {idx === 0 ? storeName : ''}
                                  </td>
                                  <td className="px-4 py-2 font-mono text-gray-500">{task.id}</td>
                                  <td className="px-4 py-2 text-gray-700">{task.packName || '—'}</td>
                                  <td className="px-4 py-2">
                                    {task.taskType === 'REMOVAL' ? (
                                      <Badge className="bg-orange-100 text-orange-800 text-xs">Removal</Badge>
                                    ) : (
                                      <Badge className="bg-blue-100 text-blue-800 text-xs">Posting</Badge>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-right">{task.targetQuantity ?? '—'}</td>
                                  <td className="px-4 py-2">{getStatusBadge(task as any)}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>
              );
            })()
          ) : (
            <div className="text-center py-12">
              <p className="text-lg font-medium text-gray-900">No assignments found</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Evidence Modal */}
      <Dialog open={evidenceModalOpen} onOpenChange={setEvidenceModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Assignment Evidence
              {selectedEvidenceId && (
                <span className="text-sm font-normal text-gray-600 ml-2">
                  (Task ID: {selectedEvidenceId})
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {evidenceLoading ? (
            <div className="text-center py-12">
              <Loader2 className="h-12 w-12 mx-auto mb-4 text-gray-400 animate-spin" />
              <p className="text-lg font-medium text-gray-900">Loading evidence...</p>
            </div>
          ) : evidenceData && evidenceData.photos && evidenceData.photos.length > 0 ? (
            <div className="space-y-6">
              {/* Photos Section */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Photos ({evidenceData.photos.length})</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {evidenceData.photos.map((photo: any, idx: number) => (
                    <div key={idx} className="space-y-2">
                      <div
                        className="aspect-square w-full cursor-pointer border-2 border-gray-200 rounded-lg overflow-hidden hover:border-primary transition-colors bg-gray-100 flex items-center justify-center"
                        onClick={() => window.open(photo.secureUrl, '_blank')}
                        title="Click to enlarge"
                      >
                        <img
                          src={photo.secureUrl}
                          alt={`Evidence ${idx + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNlNWU3ZWIiLz48dGV4dCB4PSIxMDAiIHk9IjEwMCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzY3NzA4ZiIgZm9udC1zaXplPSIxNiI+SW1hZ2UgTm90IEF2YWlsYWJsZTwvdGV4dD48L3N2Zz4=';
                          }}
                        />
                      </div>
                      <p className="text-xs text-gray-600 text-center">
                        {new Date(photo.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Store Manager Signature Section - from store_visits (per store-cycle) */}
              {(evidenceData.storeSignature?.url || evidenceData.storeSignature?.storeManagerName) && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">Store Manager Signature</h3>
                  <p className="text-xs text-gray-500 mb-2">
                    Signature is per store visit (shared across all assignments in this store-cycle)
                  </p>
                  <div 
                    className="max-w-xs cursor-pointer border-2 border-gray-200 rounded-lg overflow-hidden hover:border-primary transition-colors bg-gray-100 p-4"
                    onClick={() => {
                      if (evidenceData.storeSignature?.url) {
                        window.open(evidenceData.storeSignature.url, '_blank');
                      }
                    }}
                    title="Click to enlarge"
                  >
                    {evidenceData.storeSignature?.url ? (
                      <img
                        src={evidenceData.storeSignature.url}
                        alt="Store Manager Signature"
                        className="w-full h-auto object-contain"
                        style={{ backgroundColor: 'white' }}
                        onError={(e) => {
                          e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNlNWU3ZWIiLz48dGV4dCB4PSIxMDAiIHk9IjEwMCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzY3NzA4ZiIgZm9udC1zaXplPSIxNiI+SW1hZ2UgTm90IEF2YWlsYWJsZTwvdGV4dD48L3N2Zz4=';
                        }}
                      />
                    ) : (
                      <div className="rounded bg-white p-4 text-center">
                        <p className="text-xs text-gray-500">Typed manager sign-off</p>
                        <p className="mt-2 text-base font-semibold text-gray-800">
                          {evidenceData.storeSignature.storeManagerName}
                        </p>
                      </div>
                    )}
                    {evidenceData.storeSignature.storeManagerName && (
                      <p className="text-sm text-gray-700 mt-2 text-center">
                        Signed by: {evidenceData.storeSignature.storeManagerName}
                      </p>
                    )}
                    {evidenceData.storeSignature.signedAt && (
                      <p className="text-xs text-gray-500 text-center">
                        {new Date(evidenceData.storeSignature.signedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <Eye className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium text-gray-900">No evidence found</p>
              <p className="text-sm text-gray-600 mt-2">This assignment doesn't have any photos yet.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Phase 4: Reassign Operator Modal */}
      <Dialog open={reassignModalOpen} onOpenChange={(open) => {
        setReassignModalOpen(open);
        if (!open) {
          setSelectedReassignAssignment(null);
          setReassignOperatorId("");
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reassign Operator</DialogTitle>
          </DialogHeader>
          {selectedReassignAssignment && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Task ID:</span>
                  <span className="font-mono">{selectedReassignAssignment.id}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Store:</span>
                  <span>{selectedReassignAssignment.storeName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Campaign:</span>
                  <span>{selectedReassignAssignment.campaignName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Task Type:</span>
                  <Badge
                    variant={selectedReassignAssignment.taskType === 'REMOVAL' ? 'destructive' : 'default'}
                    className={selectedReassignAssignment.taskType === 'REMOVAL' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'}
                  >
                    {selectedReassignAssignment.taskType || 'POSTING'}
                  </Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Current Operator:</span>
                  <span>{selectedReassignAssignment.operatorName}</span>
                </div>
                {selectedReassignAssignment.assignmentSource && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Assignment Source:</span>
                    <Badge variant="outline">
                      {selectedReassignAssignment.assignmentSource === 'MANUAL_OVERRIDE' ? 'Manual' : 'Auto'}
                    </Badge>
                  </div>
                )}
              </div>

              <div>
                <Label>New Operator</Label>
                <Select
                  value={reassignOperatorId}
                  onValueChange={setReassignOperatorId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select new operator" />
                  </SelectTrigger>
                  <SelectContent>
                    {operators.map((operator: any) => (
                      <SelectItem key={operator.id} value={operator.id.toString()}>
                        {operator.name}
                        {operator.id === selectedReassignAssignment.operatorId && ' (current)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setReassignModalOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleConfirmReassign} 
                  disabled={
                    reassignOperatorMutation.isPending ||
                    !reassignOperatorId ||
                    parseInt(reassignOperatorId) === selectedReassignAssignment.operatorId
                  }
                >
                  {reassignOperatorMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Reassigning...
                    </>
                  ) : (
                    'Reassign'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
